import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { Check, X, Crown, Sparkles, Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PricingSection — shows the 3 subscription tiers.
 * Used both on the landing page (logged-out CTA) and as a standalone
 * upgrade page (logged-in users).
 */
export default function PricingSection({ embedded = false, defaultRole = 'student' }) {
    const [pricing, setPricing] = useState(null);
    const [role, setRole] = useState(defaultRole); // 'student' | 'teacher'
    const [loadingId, setLoadingId] = useState(null);
    const [trialLoading, setTrialLoading] = useState(false);
    const { user, checkAuth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Public endpoint — use axios direct (no auth required)
        axios.get('/api/subscriptions/pricing').then((r) => setPricing(r.data)).catch(() => {});
    }, []);

    // Match toggle to logged-in user's role when relevant
    useEffect(() => {
        if (user?.role === 'teacher' || user?.role === 'student') setRole(user.role);
    }, [user]);

    if (!pricing) {
        return (
            <div className="py-20 text-center">
                <Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin mx-auto" />
            </div>
        );
    }

    const trialCfg = pricing.trial || {};
    const trialEligible = trialCfg.enabled && user && user.trial?.trial_eligible && user.subscription_tier === 'free';
    const isTrialing = !!user?.trial?.is_trialing;
    const trialDaysLeft = user?.trial?.trial_days_left || 0;

    const purchase = async (tierId) => {
        if (!user) {
            navigate('/register?role=' + role + '&plan=' + tierId);
            return;
        }
        const tier = pricing.tiers.find((t) => t.id === tierId);
        const packageId = role === 'teacher' ? tier.package_teacher : tier.package_student;
        if (!packageId) return;
        setLoadingId(tierId);
        try {
            const r = await api.post('/api/payments/checkout', {
                package_id: packageId,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url; // hard redirect to Stripe Checkout
        } catch (e) {
            toast.error('Could not start payment. Please try again.');
            setLoadingId(null);
        }
    };

    const startTrial = async () => {
        if (!user) {
            navigate('/register?trial=1&role=' + role);
            return;
        }
        setTrialLoading(true);
        try {
            const r = await api.post('/api/subscriptions/start-trial', {});
            toast.success(r.data.message || 'Pro trial activated!');
            if (checkAuth) await checkAuth();
            navigate('/dashboard');
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = (typeof detail === 'object' && detail?.message) || (typeof detail === 'string' && detail) || 'Could not start trial.';
            toast.error(msg);
        } finally {
            setTrialLoading(false);
        }
    };

    return (
        <section
            id="pricing"
            className={`${embedded ? '' : 'min-h-screen'} relative z-10 px-4 sm:px-6 lg:px-8 py-20 lg:py-28`}
            data-testid="pricing-section"
        >
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-[#EDE4FF] text-[11px] font-bold text-[#7C3AED] tracking-[0.14em] mb-6 uppercase">
                        Plans &amp; Pricing
                    </div>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-950 mb-4">
                        Pick the plan that
                        <br />
                        <span className="text-[#7C3AED]">helps you grow</span>
                    </h2>
                    <p className="text-base sm:text-lg text-zinc-600 max-w-xl mx-auto">
                        Cancel anytime. Switch plans whenever. Built for A-Level students &amp; teachers.
                    </p>
                </div>

                {/* Student/Teacher toggle */}
                <div className="flex justify-center mb-6" data-testid="pricing-role-toggle">
                    <div className="inline-flex bg-zinc-100 p-1 rounded-full">
                        {['student', 'teacher'].map((r) => (
                            <button
                                key={r}
                                onClick={() => setRole(r)}
                                className={`px-5 py-2 text-sm font-bold rounded-full transition-all capitalize ${
                                    role === r
                                        ? 'bg-white text-[#7C3AED] shadow-sm'
                                        : 'text-zinc-500 hover:text-zinc-700'
                                }`}
                                data-testid={`pricing-role-${r}`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Trial banner */}
                {trialCfg.enabled && !isTrialing && (!user || trialEligible) && (
                    <div
                        className="max-w-3xl mx-auto mb-10 rounded-2xl p-5 sm:p-6 bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white shadow-[0_18px_45px_-15px_rgba(124,58,237,0.55)] flex flex-col sm:flex-row items-center gap-4 sm:gap-6"
                        data-testid="trial-banner"
                    >
                        <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                            <Gift className="w-6 h-6" />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                            <div className="font-black text-lg sm:text-xl tracking-tight">{trialCfg.headline || `Try Pro free for ${trialCfg.days} days`}</div>
                            <div className="text-sm text-white/80 mt-0.5">{trialCfg.subheadline || 'No credit card required. Cancel anytime.'}</div>
                        </div>
                        <button
                            onClick={startTrial}
                            disabled={trialLoading}
                            className="bg-white text-[#7C3AED] font-black text-sm px-6 py-3 rounded-xl hover:bg-zinc-100 transition-colors inline-flex items-center gap-2 disabled:opacity-70 whitespace-nowrap"
                            data-testid="trial-banner-cta"
                        >
                            {trialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            {trialLoading ? 'Activating…' : (user ? 'Start free trial' : 'Sign up & try free')}
                        </button>
                    </div>
                )}
                {isTrialing && (
                    <div className="max-w-3xl mx-auto mb-10 rounded-2xl p-4 bg-amber-50 border-2 border-amber-200 text-amber-900 flex items-center gap-3" data-testid="trial-active-banner">
                        <Crown className="w-5 h-5 fill-amber-500 text-amber-500 flex-shrink-0" />
                        <div className="text-sm font-bold flex-1">
                            Pro Trial active — {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left.
                            <span className="font-normal text-amber-800/80"> Pick a plan below to keep your access.</span>
                        </div>
                    </div>
                )}

                {/* Tier cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {pricing.tiers.map((t) => {
                        const price = role === 'teacher' ? t.price_teacher : t.price_student;
                        const isCurrent = user && (user.subscription_tier === t.id || (!user.subscription_tier && t.id === 'free'));
                        const isPopular = t.popular;
                        return (
                            <div
                                key={t.id}
                                className={`relative bg-white rounded-3xl p-7 transition-all ${
                                    isPopular
                                        ? 'border-2 border-[#7C3AED] shadow-[0_20px_50px_-15px_rgba(124,58,237,0.45)] md:scale-105'
                                        : 'border border-zinc-200 shadow-[0_4px_14px_-4px_rgba(0,0,0,0.06)]'
                                }`}
                                data-testid={`pricing-card-${t.id}`}
                            >
                                {isPopular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#7C3AED] text-white text-[11px] font-black uppercase tracking-wider">
                                        <Sparkles className="w-3 h-3" /> Most Popular
                                    </div>
                                )}
                                <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                                    {t.id === 'pro' && (
                                        <span className="inline-flex items-center gap-1.5 text-amber-500">
                                            <Crown className="w-3.5 h-3.5 fill-amber-500" /> Pro
                                        </span>
                                    )}
                                    {t.id !== 'pro' && t.name}
                                </div>
                                <h3 className="text-3xl font-black text-zinc-950 mb-1">{t.name}</h3>
                                <p className="text-sm text-zinc-500 mb-6">{role === 'teacher' ? (t.tagline_teacher || t.tagline) : (t.tagline_student || t.tagline)}</p>
                                <div className="mb-6">
                                    {t.id === 'free' ? (
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-5xl font-black text-zinc-950">$0</span>
                                            <span className="text-sm text-zinc-500">forever</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-5xl font-black text-zinc-950">${price}</span>
                                            <span className="text-sm text-zinc-500">/month</span>
                                        </div>
                                    )}
                                </div>

                                {/* Features */}
                                <ul className="space-y-2.5 mb-7">
                                    {(role === 'teacher' ? (t.features_teacher || t.features || []) : (t.features_student || t.features || [])).map((f, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-700">
                                            <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                    {(role === 'teacher' ? (t.excluded_teacher || t.excluded || []) : (t.excluded_student || t.excluded || [])).map((f, i) => (
                                        <li key={`x-${i}`} className="flex items-start gap-2.5 text-sm text-zinc-400">
                                            <X className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                            <span className="line-through">{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA */}
                                {t.id === 'free' ? (
                                    user ? (
                                        <button
                                            disabled
                                            className="w-full py-3.5 rounded-xl bg-zinc-100 text-zinc-500 font-bold text-sm cursor-not-allowed"
                                            data-testid={`pricing-cta-${t.id}`}
                                        >
                                            {isCurrent ? 'Your current plan' : 'Free tier'}
                                        </button>
                                    ) : (
                                        <Link
                                            to="/register"
                                            className="block text-center w-full py-3.5 rounded-xl bg-zinc-950 text-white font-bold text-sm hover:bg-zinc-800 transition-colors"
                                            data-testid={`pricing-cta-${t.id}`}
                                        >
                                            Sign up free
                                        </Link>
                                    )
                                ) : (
                                    <button
                                        onClick={() => purchase(t.id)}
                                        disabled={loadingId === t.id || isCurrent}
                                        className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all inline-flex items-center justify-center gap-2 ${
                                            isCurrent
                                                ? 'bg-zinc-100 text-zinc-500 cursor-not-allowed'
                                                : isPopular
                                                ? 'bg-[#7C3AED] text-white hover:bg-[#6D28D9] shadow-[0_6px_20px_-6px_rgba(124,58,237,0.55)]'
                                                : 'bg-zinc-950 text-white hover:bg-zinc-800'
                                        }`}
                                        data-testid={`pricing-cta-${t.id}`}
                                    >
                                        {loadingId === t.id ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                                            </>
                                        ) : isCurrent ? (
                                            'Your current plan'
                                        ) : (
                                            <>Purchase {t.name}</>
                                        )}
                                    </button>
                                )}
                                {t.id === 'pro' && trialCfg.enabled && !isTrialing && (!user || trialEligible) && (
                                    <button
                                        onClick={startTrial}
                                        disabled={trialLoading}
                                        className="mt-3 w-full py-2.5 rounded-xl bg-amber-50 text-amber-900 font-bold text-xs border-2 border-amber-200 hover:bg-amber-100 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-70"
                                        data-testid={`pricing-trial-cta-${t.id}`}
                                    >
                                        {trialLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gift className="w-3.5 h-3.5" />}
                                        {trialLoading ? 'Activating…' : `Or try free for ${trialCfg.days || 7} days`}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                <p className="text-center text-xs text-zinc-500 mt-8">
                    Secure payment by Stripe · Cancel anytime · 30-day access per billing cycle
                </p>
            </div>
        </section>
    );
}
